import { FC, useEffect, useRef, useState } from 'react';
import { Box, Skeleton } from '@hopskipdrive/react-components';
import { HIDDEN_SERVICE_CODES } from '@rideiq/riders/constants/riderServices';
import { Button } from '@rideiq/shared/components/Button';
import { useAnnouncer } from '@rideiq/shared/contexts/LiveRegionAnnouncerProvider';

import { analyticsEditRiderSafetySettings } from '../../../api/services/Analytics';
import { updatePartialRider } from '../../../api/services/Riders';
import { actions, Can, subjects } from '../../../contexts/AbilityContext';
import { useFlags } from '../../../contexts/LDContext';
import { useToastStore } from '../../../shared/stores/toastStore';
import { RiderCard } from '../../components/RiderCard';
import { useRiderQuery } from '../../hooks/useRiderQuery';

import { EditModal } from './EditModal';
import { Content, Divider, Label, List } from './SafetySettings.styles';
import { ISafetySettings } from './types';

export const SafetySettings: FC<ISafetySettings & { isFetching: boolean; id: string; dateOfBirth?: string }> = ({
  isFetching,
  id,
  dateOfBirth,
  ...safetySettings
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSafetySettings] = useState(safetySettings);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { addToast } = useToastStore();
  const { iepStructuredNotesEnabled } = useFlags();
  const useNewForm = iepStructuredNotesEnabled;
  const { fetchRiderBySlug } = useRiderQuery();
  const { announce } = useAnnouncer();

  const editButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    setSafetySettings(safetySettings);
  }, [
    dateOfBirth,
    safetySettings.boosterSeat,
    safetySettings.frontSeatAuthorized,
    safetySettings.mustBeMet,
    safetySettings.codeWord,
    safetySettings.services,
  ]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    const response = await updatePartialRider(id, data);

    if (response.type === 'error') {
      response.error.errors?.forEach((error) => addToast({ severity: 'error', message: error }));
    } else {
      analyticsEditRiderSafetySettings(id, data.services);
      setIsOpen(false);
      announce('Safety settings saved successfully.');
      await fetchRiderBySlug(id);
    }
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (!isOpen && editButtonRef.current) {
      editButtonRef.current.focus();
    }
  }, [isOpen]);

  return (
    <RiderCard
      gradientOverlay={true}
      height="227px"
      heading={
        <Box display="flex" justifyContent="space-between" alignContent="center">
          <Label>Safety Settings</Label>
          {!useNewForm && (
            <Can I={actions.edit} a={subjects.rider}>
              <Button
                ref={editButtonRef}
                data-testid="edit-safety-settings-btn"
                color="secondary"
                onClick={() => setIsOpen(true)}
                disabled={isFetching}
                size="small"
                style={{ minWidth: '68px', height: '25px' }}
              >
                Edit
              </Button>
            </Can>
          )}
        </Box>
      }
      data-testid="rider-safety-settings-card"
    >
      <Content>
        {isFetching && (
          <>
            <Skeleton width={120} />
            <Skeleton width={100} />
          </>
        )}
        {!isFetching && settings && (
          <SafetySettingChecks
            boosterSeat={settings.boosterSeat}
            frontSeatAuthorized={settings.frontSeatAuthorized}
            mustBeMet={settings.mustBeMet}
          />
        )}
        <Divider aria-hidden="true" />
        <Label>Code Word</Label>
        <p>{settings.codeWord || <Skeleton width={100} />}</p>
        <Divider aria-hidden="true" />
        <Label>Rider Notes</Label>
        <p>
          {(!isFetching && (settings.riderNotes ? settings.riderNotes : <i>{'No rider notes specified'}</i>)) || (
            <>
              <Skeleton width={350} />
              <Skeleton width={250} />
            </>
          )}
        </p>
        <>
          <Divider aria-hidden="true" />
          <Label>Services</Label>
          {!isFetching && settings.services && <ServicesChecks services={settings.services} />}
        </>
      </Content>
      {!isFetching && dateOfBirth && settings && (
        <EditModal
          isOpen={isOpen}
          onCancel={() => setIsOpen(false)}
          onSubmit={onSubmit}
          safetySettings={settings}
          dateOfBirth={dateOfBirth}
          isSubmitting={isSubmitting}
          riderId={id}
        />
      )}
    </RiderCard>
  );
};

const SafetySettingChecks = ({ boosterSeat, frontSeatAuthorized, mustBeMet }) => {
  if (!boosterSeat && !frontSeatAuthorized && !mustBeMet) {
    return <i>{'No safety settings specified'}</i>;
  }

  return (
    <List>
      {boosterSeat && <li>Needs a booster seat</li>}
      {frontSeatAuthorized && <li>Can Sit Up Front</li>}
      {mustBeMet && <li>Must be met</li>}
    </List>
  );
};

const ServicesChecks = ({ services }) => {
  const allowedServices = services.filter((service) => !HIDDEN_SERVICE_CODES.includes(service.code));

  if (allowedServices.length === 0) {
    return <i>{'No services specified'}</i>;
  }

  return (
    <List>
      {allowedServices.map((service, index) => (
        <li key={index}>
          {service.name} ({service.code})
        </li>
      ))}
    </List>
  );
};
